import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./ImageViewer.css";

type ImageViewerProps = {
  isOpen: boolean;
  imageUrl: string;
  onClose: () => void;
};

export const ImageViewer = ({
  isOpen,
  imageUrl,
  onClose
}: ImageViewerProps) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      // Guardar la posición actual del scroll
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      // Prevenir scroll del body cuando el visor está abierto
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.right = "0";
      document.body.style.bottom = "0";

      setImageError(false);
      setIsLoading(true);

      return () => {
        // Restaurar el scroll y estilos del body
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.bottom = "";
        window.scrollTo(scrollX, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="image-viewer-overlay" onClick={onClose}>
      <button
        className="image-viewer-close"
        onClick={onClose}
        aria-label="Cerrar"
        title="Cerrar (Esc)"
      >
        <i className="fa-light fa-times"></i>
      </button>

      <div
        className="image-viewer-container"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading && !imageError && (
          <div className="image-viewer-loading">
            <i
              className="fas fa-spinner-third fa-spin"
              style={{ fontSize: "48px", color: "white" }}
            ></i>
          </div>
        )}

        {imageError ? (
          <div className="image-viewer-error">
            <i
              className="fa-light fa-exclamation-triangle"
              style={{ fontSize: "48px", color: "white", marginBottom: "16px" }}
            ></i>
            <p>No se pudo cargar la imagen</p>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt="Imagen ampliada"
            className="image-viewer-img"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: isLoading ? "none" : "block" }}
          />
        )}
      </div>
    </div>,
    document.body
  );
};
